import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, Badge, SimpleGrid, List, ListItem, ListIcon, Button } from "@chakra-ui/react";
import { FaCheckCircle, FaSolarPanel, FaDollarSign, FaChartLine, FaFileSignature } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const SolutionsFinanceSolar: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solutions/finance-solar"
        fallbackTitle="Finance + Solar Solutions"
        fallbackDescription="Seamless financing options for solar installations and equipment"
      />
      <NavbarWithCallToAction />

      {/* Hero Section with Benefit Badge */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge bg="#C2A878" color={brandConfig.colors.primary} fontSize="md" px={4} py={2} borderRadius="full">
              No Upfront Costs
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Finance + Solar Solutions
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Finance your solar installation with flexible loan options
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Two-Column: Problem vs Solution */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box p={8} bg="gray.50" borderRadius="lg" borderLeft="4px solid" borderColor="gray.400">
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>The Problem</Heading>
            <List spacing={3}>
              <ListItem color="gray.700">High upfront solar installation costs</ListItem>
              <ListItem color="gray.700">Cash flow constraints for businesses</ListItem>
              <ListItem color="gray.700">Waiting years to see ROI</ListItem>
              <ListItem color="gray.700">Missing out on energy savings now</ListItem>
            </List>
          </Box>
          <Box p={8} bg="white" borderRadius="lg" borderLeft="4px solid" borderColor="#C2A878" boxShadow="md">
            <Heading size="md" mb={4} color={brandConfig.colors.primary}>Our Solution</Heading>
            <List spacing={3}>
              <ListItem color="gray.700">
                <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                Zero upfront payment options
              </ListItem>
              <ListItem color="gray.700">
                <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                Monthly payments lower than energy savings
              </ListItem>
              <ListItem color="gray.700">
                <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                Immediate cash flow positive
              </ListItem>
              <ListItem color="gray.700">
                <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                Start saving from day one
              </ListItem>
            </List>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Finance Options Cards (3 different loan types) */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={8} textAlign="center">Choose Your Finance Option</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="md" _hover={{ boxShadow: "xl", transform: "translateY(-4px)" }} transition="all 0.3s">
              <VStack align="start" spacing={4}>
                <HStack>
                  <Box as={FaDollarSign} color={brandConfig.colors.primary} size="24px" />
                  <Heading size="md">Standard Loan</Heading>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold" color={brandConfig.colors.primary}>5.9% APR</Text>
                <Text color="gray.600">Fixed rate, 5-10 year term</Text>
                <List spacing={2} flex="1">
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Competitive interest rates
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Flexible repayment terms
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    No early exit fees
                  </ListItem>
                </List>
              </VStack>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="md" border="2px solid" borderColor="#C2A878" _hover={{ boxShadow: "xl", transform: "translateY(-4px)" }} transition="all 0.3s">
              <Badge bg="#C2A878" color={brandConfig.colors.primary} mb={2}>Most Popular</Badge>
              <VStack align="start" spacing={4}>
                <HStack>
                  <Box as={FaSolarPanel} color={brandConfig.colors.primary} size="24px" />
                  <Heading size="md">Solar-Specific Loan</Heading>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold" color={brandConfig.colors.primary}>4.9% APR</Text>
                <Text color="gray.600">Optimized for solar, 7-12 year term</Text>
                <List spacing={2} flex="1">
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Lowest rates for solar
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Tax benefit optimization
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Includes battery storage
                  </ListItem>
                </List>
              </VStack>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="md" _hover={{ boxShadow: "xl", transform: "translateY(-4px)" }} transition="all 0.3s">
              <VStack align="start" spacing={4}>
                <HStack>
                  <Box as={FaChartLine} color={brandConfig.colors.primary} size="24px" />
                  <Heading size="md">Business Loan</Heading>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold" color={brandConfig.colors.primary}>6.5% APR</Text>
                <Text color="gray.600">Commercial finance, up to 15 years</Text>
                <List spacing={2} flex="1">
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Tax-deductible payments
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Preserve business capital
                  </ListItem>
                  <ListItem fontSize="sm">
                    <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                    Large installation support
                  </ListItem>
                </List>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Solar ROI Calculator Teaser */}
      <Container maxW="container.xl" py={16}>
        <Box bg={brandConfig.colors.primary} p={10} borderRadius="xl" color="white" textAlign="center">
          <Heading size="lg" mb={4}>Calculate Your Solar Savings</Heading>
          <Text fontSize="lg" mb={6} opacity={0.9}>
            See exactly how much you'll save with our interactive ROI calculator
          </Text>
          <Button size="lg" bg="#C2A878" color={brandConfig.colors.primary} _hover={{ bg: "#b09060" }} fontWeight="bold">
            Try the Calculator
          </Button>
        </Box>
      </Container>

      {/* Step-by-Step Application Process */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={12} textAlign="center">Simple 3-Step Application Process</Heading>
          <HStack spacing={8} align="stretch" justify="center" flexDirection={{ base: "column", md: "row" }}>
            <VStack flex="1" bg="white" p={8} borderRadius="lg" boxShadow="sm" position="relative">
              <Box position="absolute" top="-20px" bg={brandConfig.colors.primary} color="white" w="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="xl" fontWeight="bold">
                1
              </Box>
              <Box as={FaFileSignature} size="48px" color={brandConfig.colors.primary} mb={4} mt={4} />
              <Heading size="md" mb={2}>Submit Application</Heading>
              <Text color="gray.600" textAlign="center">
                Quick 5-minute online form with instant pre-approval
              </Text>
            </VStack>

            <VStack flex="1" bg="white" p={8} borderRadius="lg" boxShadow="sm" position="relative">
              <Box position="absolute" top="-20px" bg={brandConfig.colors.primary} color="white" w="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="xl" fontWeight="bold">
                2
              </Box>
              <Box as={FaSolarPanel} size="48px" color={brandConfig.colors.primary} mb={4} mt={4} />
              <Heading size="md" mb={2}>Design & Approve</Heading>
              <Text color="gray.600" textAlign="center">
                We design your custom solar system and finalize loan terms
              </Text>
            </VStack>

            <VStack flex="1" bg="white" p={8} borderRadius="lg" boxShadow="sm" position="relative">
              <Box position="absolute" top="-20px" bg={brandConfig.colors.primary} color="white" w="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="xl" fontWeight="bold">
                3
              </Box>
              <Box as={FaCheckCircle} size="48px" color={brandConfig.colors.primary} mb={4} mt={4} />
              <Heading size="md" mb={2}>Install & Save</Heading>
              <Text color="gray.600" textAlign="center">
                Professional installation and start saving immediately
              </Text>
            </VStack>
          </HStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <Box textAlign="center">
          <Heading size="xl" mb={4}>Ready to Get Started?</Heading>
          <Text fontSize="lg" color="gray.600" mb={8}>
            Apply now and get pre-approved in minutes
          </Text>
          <HStack spacing={4} justify="center">
            <Button size="lg" bg="#C2A878" color={brandConfig.colors.primary} _hover={{ bg: "#b09060" }} fontWeight="bold" onClick={() => navigate("/contact")}>
              Apply Now
            </Button>
            <Button size="lg" variant="outline" borderColor={brandConfig.colors.primary} color={brandConfig.colors.primary} onClick={() => navigate("/contact")}>
              Speak to an Advisor
            </Button>
          </HStack>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolutionsFinanceSolar;
